import React, { useState, useEffect, useRef } from "react";
import useOutsideClick from "../../hooks/useOutsideClick";

/**
 * ReactionsPopup Component
 * Displays a horizontal popup with reaction options on hover
 * Similar to Facebook/Instagram reactions
 */
const ReactionsPopup = ({
  reactions = [],
  currentUserReaction = null,
  reactionSummary = [], // Array of { reaction_type_id, emoji, name, count }
  onReactionSelect,
  onClose,
  position = "top", // 'top' or 'bottom'
}) => {
  const [hoveredReaction, setHoveredReaction] = useState(null);
  const popupRef = useRef(null);

  // Don't close on outside click - let parent handle it
  // useOutsideClick(popupRef, () => {
  //   if (onClose) onClose();
  // });

  // Sort reactions by order
  const sortedReactions = [...reactions].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  // Helper function to get count for a reaction
  const getReactionCount = (reactionId) => {
    const summaryItem = reactionSummary.find(
      (item) => item.reaction_type_id === reactionId || item.id === reactionId,
    );
    return summaryItem?.count || 0;
  };

  const handleReactionClick = (reaction) => {
    if (onReactionSelect) {
      onReactionSelect(reaction);
    }
  };

  const handleReactionHover = (reaction) => {
    setHoveredReaction(reaction.id);
  };

  const handleMouseLeave = () => {
    setHoveredReaction(null);
  };

  return (
    <div
      ref={popupRef}
      style={{
        position: "absolute",
        [position === "top" ? "bottom" : "top"]: "100%",
        left: "0",
        marginBottom: position === "top" ? "8px" : "0",
        marginTop: position === "bottom" ? "8px" : "0",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 12px",
        background: "rgba(28, 41, 52, 0.95)",
        backdropFilter: "blur(10px) saturate(120%)",
        WebkitBackdropFilter: "blur(10px) saturate(120%)",
        border: "1px solid rgba(0, 140, 255, 0.3)",
        borderRadius: "24px",
        boxShadow:
          "0 4px 20px rgba(0, 0, 0, 0.4), 0 0 10px rgba(0, 140, 255, 0.2)",
        animation: "fadeInScale 0.2s ease-out",
        pointerEvents: "auto",
      }}
      onMouseEnter={() => {
        // Keep popup open when hovering over it
      }}
      onMouseLeave={handleMouseLeave}
    >
      {sortedReactions.map((reaction) => {
        // Check if this is the current user's reaction
        // Handle both formats: reaction_type_id or id
        const currentReactionId =
          currentUserReaction?.reaction_type_id || currentUserReaction?.id;
        const isCurrentUserReaction = currentReactionId === reaction.id;
        const isHovered = hoveredReaction === reaction.id;
        const reactionCount = getReactionCount(reaction.id);

        return (
          <div
            key={reaction.id}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            <div
              onClick={() => handleReactionClick(reaction)}
              onMouseEnter={() => handleReactionHover(reaction)}
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: "50%",
                transition: "all 0.2s ease",
                transform: isHovered
                  ? "scale(1.3) translateY(-4px)"
                  : isCurrentUserReaction
                    ? "scale(1.1)"
                    : "scale(1)",
                backgroundColor: isCurrentUserReaction
                  ? "rgba(0, 140, 255, 0.2)"
                  : isHovered
                    ? "rgba(255, 255, 255, 0.1)"
                    : "transparent",
                border: isCurrentUserReaction
                  ? "2px solid rgba(0, 140, 255, 0.5)"
                  : "2px solid transparent",
                position: "relative",
              }}
            >
              <span
                style={{
                  fontSize: isHovered ? "28px" : "24px",
                  lineHeight: "1",
                  transition: "font-size 0.2s ease",
                  filter: isCurrentUserReaction
                    ? "drop-shadow(0 0 4px rgba(0, 140, 255, 0.6))"
                    : "none",
                }}
              >
                {reaction.emoji}
              </span>
              {/* Tooltip on hover */}
              {isHovered && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginBottom: "8px",
                    padding: "4px 8px",
                    background: "rgba(0, 0, 0, 0.8)",
                    color: "#fff",
                    fontSize: "12px",
                    borderRadius: "6px",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    animation: "fadeIn 0.15s ease-out",
                  }}
                >
                  {reaction.name}
                </div>
              )}
            </div>
            {/* Count displayed outside below the emoji container */}
            <span
              style={{
                fontSize: "11px",
                color: "#fff",
                fontWeight: "600",
                lineHeight: "1",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.8)",
                minHeight: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {reactionCount > 0 ? reactionCount : ""}
            </span>
          </div>
        );
      })}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ReactionsPopup;
